import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getQueries, postQuery, editQuery, deleteQuery } from "./index";
import { CategoryQuery } from "../../models/CategoryQuery";
import { Portfolio } from "@/types/Portfolio";
import { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import { FilterNotifications } from "@/types/notifications";
import useAuth from "@/hooks/useAuth";

interface RawHeadlineQuery {
  id: string | number;
  name: string;
  tags: string[];
  search_keyword: string;
  portfolios?: Portfolio[];
  tags_watchlists?: TagsWatchlist[];
  keyword_op: "AND" | "OR";
  filter_op: "AND" | "OR";
  keyword_watchlist_op?: "AND" | "OR";
  notifications?: FilterNotifications;
  owner_type?: "USER" | "NEWSQUAWK" | "EXTERNAL";
}

export const transformHeadlineQueries = (
  input: RawHeadlineQuery[]
): CategoryQuery[] => {
  return input.map((query) => ({
    id: Number(query.id),
    queryName: query.name,
    categories: query.tags,
    searchKeyword: query.search_keyword,
    portfolios: query.portfolios || [],
    portfolioIds: query.portfolios?.map((portfolio) => portfolio.id) || [],
    tagsWatchlists: query.tags_watchlists || [],
    tagsWatchlistIds: query.tags_watchlists?.map((tw) => tw.id) || [],
    keywordOp: query.keyword_op,
    filterMode: query.filter_op,
    keywordWatchlistOp: query.keyword_watchlist_op,
    notifications: query.notifications,
    owner_type: query.owner_type,
  }));
};

export const useHeadlineQueriesCRUD = () => {
  const queryClient = useQueryClient();

  const { authenticated } = useAuth();

  // Fetch headline queries
  const queriesQuery = useQuery({
    queryKey: ["headlineQueries"],
    queryFn: () => getQueries(),
    select: (res) => transformHeadlineQueries(res?.data || []),
    enabled: authenticated,
  });

  // Create headline query
  const createMutation = useMutation({
    mutationFn: postQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
    },
  });

  // Edit headline query
  const editMutation = useMutation({
    mutationFn: editQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
    },
  });

  // Delete headline query
  const deleteMutation = useMutation({
    mutationFn: deleteQuery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
    },
  });

  return {
    queriesQuery,
    createMutation,
    editMutation,
    deleteMutation,
  };
};
